import React, { useCallback, useEffect, useState } from "react";
import { Mail } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { previewInvoiceDueReminder, sendInvoiceDueReminder } from "../../lib/revenueApi";
import { formatApiDetail } from "../../lib/api";

function wrapPreviewHtml(fragment) {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <style>
    @font-face {
      font-family: 'Nohemi';
      src: url('/fonts/Nohemi-Light.otf') format('opentype');
      font-weight: 300;
      font-style: normal;
    }
    body { margin: 0; font-family: 'Nohemi', -apple-system, sans-serif; }
  </style>
</head>
<body>${fragment}</body>
</html>`;
}

export default function InvoiceDueReminderDialog({ invoice, open, onOpenChange, onSent }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const loadPreview = useCallback(async () => {
    if (!invoice?.id) return;
    setLoading(true);
    setError("");
    setSent(false);
    try {
      const data = await previewInvoiceDueReminder(invoice.id);
      setPreview(data);
    } catch (e) {
      setPreview(null);
      setError(formatApiDetail(e.response?.data?.detail, "Could not load email preview."));
    } finally {
      setLoading(false);
    }
  }, [invoice?.id]);

  useEffect(() => {
    if (open && invoice?.id) loadPreview();
    if (!open) {
      setPreview(null);
      setError("");
      setSent(false);
    }
  }, [open, invoice?.id, loadPreview]);

  const handleSend = async () => {
    if (!invoice?.id || !preview?.eligible) return;
    setSending(true);
    setError("");
    try {
      await sendInvoiceDueReminder(invoice.id);
      setSent(true);
      onSent?.();
    } catch (e) {
      setError(formatApiDetail(e.response?.data?.detail, "Could not send reminder."));
    } finally {
      setSending(false);
    }
  };

  if (!invoice) return null;

  const canSend = preview?.eligible && !sent;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-[calc(100%-2rem)] p-0 gap-0 overflow-hidden rounded-[var(--t-event-pill-radius)]">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-[var(--t-border)] text-left">
          <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
            <Mail size={18} strokeWidth={1.4} />
            Payment reminder
          </DialogTitle>
          <DialogDescription className="text-left">
            {invoice.invoiceNumber} · {invoice.client}
            {preview?.to_email ? (
              <span className="block mt-1 t-meta text-xs">To: {preview.to_email}</span>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <p className="t-meta text-sm py-8 text-center">Loading preview…</p>
          ) : null}

          {error ? <p className="text-sm text-[var(--t-error)]">{error}</p> : null}

          {preview && !preview.eligible ? (
            <p className="t-meta text-sm">{preview.ineligible_reason || "This invoice cannot receive a reminder."}</p>
          ) : null}

          {preview?.eligible ? (
            <>
              <div className="t-meta text-xs space-y-1">
                <p>
                  <span className="label-eyebrow !text-[9px] mr-2">Subject</span>
                  {preview.subject}
                </p>
                <p>
                  <span className="label-eyebrow !text-[9px] mr-2">Balance due</span>
                  {preview.outstanding_display}
                  <span className="mx-2">·</span>
                  Due {preview.due_display}
                  {preview.overdue ? (
                    <span className="ml-2 text-[var(--t-error)] uppercase tracking-widest text-[9px]">
                      Overdue
                    </span>
                  ) : null}
                </p>
              </div>

              <div className="border border-[var(--t-border)] rounded-[var(--t-event-pill-radius)] overflow-hidden bg-[#FAFBFC]">
                <iframe
                  title="Payment reminder email preview"
                  srcDoc={wrapPreviewHtml(preview.html)}
                  className="w-full min-h-[420px] border-0 bg-white"
                  data-testid="invoice-due-reminder-preview"
                />
              </div>
            </>
          ) : null}

          {sent ? (
            <p className="text-sm text-[var(--t-accent)]" data-testid="invoice-due-reminder-sent">
              Reminder sent to {preview?.to_email}.
            </p>
          ) : null}
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-2 px-6 py-4 border-t border-[var(--t-border)]">
          <Button
            type="button"
            variant="outline"
            className="!rounded-[var(--t-event-pill-radius)] flex-1"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
          <Button
            type="button"
            className="!rounded-[var(--t-event-pill-radius)] flex-1"
            disabled={!canSend || sending || loading}
            onClick={handleSend}
            data-testid="invoice-due-reminder-send"
          >
            {sending ? "Sending…" : sent ? "Sent" : "Send reminder"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
